import { type SyntheticEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import {
  FEEDBACK_AREAS,
  FEEDBACK_CATEGORIES,
  type FeedbackArea,
  type FeedbackCategory,
  useCreateSuggestion,
} from '@/entities/feedback';
import { useErrorMessage } from '@/shared/i18n';
import { Button, Combobox, TextAreaField, TextField } from '@/shared/ui';

import { AttachmentPicker } from './AttachmentPicker';
import { areaLabel, categoryLabel } from './labels';
import { suggestedArea } from './suggested-area';
import { useAttachmentDraft } from './use-attachment-draft';
import styles from './NewSuggestionForm.module.css';

const TITLE_MAX = 200;
const DESCRIPTION_MAX = 10_000;

function newKey(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function NewSuggestionForm() {
  const { t } = useTranslation();
  const getMessage = useErrorMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const create = useCreateSuggestion();
  const draft = useAttachmentDraft();
  const key = useRef(newKey());
  // A second click can arrive before React re-renders with the pending state.
  const sending = useRef(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<FeedbackCategory>('improvement');
  const [description, setDescription] = useState('');
  const [area, setArea] = useState<FeedbackArea | null>(() => suggestedArea(location.state));
  const [touched, setTouched] = useState(false);

  const titleMissing = title.trim() === '';
  const descriptionMissing = description.trim() === '';
  const busy = create.isPending || draft.uploading;

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    setTouched(true);
    if (titleMissing || descriptionMissing || busy || sending.current) return;
    sending.current = true;
    create.mutate(
      {
        title: title.trim(),
        category,
        description: description.trim(),
        area,
        attachment_ids: draft.ids,
        client_key: key.current,
      },
      {
        onSuccess: (suggestion) => {
          draft.clear();
          void navigate(`/app/feedback/${suggestion.id}`, { replace: true });
        },
        onError: () => {
          sending.current = false;
        },
      },
    );
  };

  return (
    <form className={styles.form} onSubmit={submit} onPaste={draft.onPaste} noValidate>
      <TextField
        label={t('feedback.form.title')}
        value={title}
        maxLength={TITLE_MAX}
        autoComplete="off"
        autoFocus
        error={touched && titleMissing ? t('feedback.form.required') : undefined}
        onChange={(event) => {
          setTitle(event.target.value);
        }}
      />
      <fieldset className={styles.categories}>
        <legend className={styles.legend}>{t('feedback.form.category')}</legend>
        <div className={styles.choices}>
          {FEEDBACK_CATEGORIES.map((value) => (
            <label
              key={value}
              className={`${styles.choice} ${category === value ? styles.picked : ''}`}
            >
              <input
                type="radio"
                name="feedback-category"
                value={value}
                checked={category === value}
                onChange={() => {
                  setCategory(value);
                }}
              />
              {categoryLabel(t, value)}
            </label>
          ))}
        </div>
      </fieldset>
      <TextAreaField
        label={t('feedback.form.description')}
        value={description}
        rows={8}
        maxLength={DESCRIPTION_MAX}
        placeholder={t(
          category === 'problem' ? 'feedback.form.placeholderProblem' : 'feedback.form.placeholder',
        )}
        error={touched && descriptionMissing ? t('feedback.form.required') : undefined}
        onChange={(event) => {
          setDescription(event.target.value);
        }}
      />
      <AttachmentPicker draft={draft} />
      <details
        className={styles.more}
        open={area !== null && suggestedArea(location.state) !== null}
      >
        <summary className={styles.summary}>{t('feedback.form.more')}</summary>
        <div className={styles.moreBody}>
          <Combobox
            label={t('feedback.form.area')}
            value={area ?? 'none'}
            searchable={false}
            options={[
              { value: 'none', label: t('feedback.area.none') },
              ...FEEDBACK_AREAS.map((value) => ({ value, label: areaLabel(t, value) })),
            ]}
            onChange={(value) => {
              setArea(FEEDBACK_AREAS.find((a) => a === value) ?? null);
            }}
          />
          <p className={styles.note}>{t('feedback.form.areaNote')}</p>
        </div>
      </details>
      {create.isError && (
        <p className={styles.error} role="alert">
          {getMessage(create.error)}
        </p>
      )}
      <div className={styles.actions}>
        <Button
          onClick={() => {
            void navigate('/app/feedback');
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" disabled={busy}>
          {create.isPending ? t('feedback.form.sending') : t('feedback.form.submit')}
        </Button>
      </div>
    </form>
  );
}
